import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import TextButton from "@shared/v2/TextButton";
import { Popover, PopoverItem, PopoverContent, PopoverTrigger } from "@shared/v2/Popover";
import { EyeSolidV6, PlusSolidV6, TriangleExclamationSolidV6 } from "@shared/v2/Icomoon";
import Button from "@shared/v2/Button";
import * as css from "./person-detail-styles.module.css";
import CmaReport from "../../CmaReport";
import CmaReportPreview from "../../CmaReport/cmaReportPreview";
import Loading from "../../Goals/components/Loader";
import CollapsibleElement from "./CollapsibleElement";
import { useMainEmail } from "../../reducers/personalDetailReducer/selectors";

const getApiHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  "x-csrf-token": window.ReactOnRails.authenticityToken(),
});

const cmaUrl = process.env.NODE_ENV === "production" ? "www.brivitycma.com" : "www.staging.brivitycma.com";

const useCmaReports = ({ person }) => {
  const [cmaReports, setCmaReports] = useState([]);
  const [isCmaReportModalOpen, setIsCmaReportModalOpen] = useState(false);

  useEffect(() => {
    if (person.data?.attributes.uuid) {
      axios
        .get(`/cma_reports?uuid=${person.data?.attributes.uuid}`, { headers: getApiHeaders() })
        .then(({ data }) => setCmaReports(data))
        // TODO: handle error
        .catch(console.log);
    }
  }, [person.data?.attributes.uuid]);

  return {
    actions: {
      deleteCmaReport: ({ id }) =>
        axios
          .delete(`/cma_reports/${id}`, { headers: getApiHeaders() })
          .then(() => setCmaReports((currentReports) => currentReports.filter((r) => r.id !== id)))
          .catch((err) => console.log(err)),
      toggleModal: (value) => setIsCmaReportModalOpen((o) => (typeof value === "boolean" ? value : !o)),
    },
    data: { cmaReports, isCmaReportModalOpen },
  };
};

const CmaReports = ({ hasApprovedMls, loading }) => {
  const person = useSelector((state) => state.personDetail.person);
  const reportPreviewPerson = person.data?.attributes || {};

  const hasEmail = Boolean(useMainEmail());
  // Disabling before the person loads is safe; claiming they have no email is not.
  const personLoaded = Boolean(person.data);

  const {
    actions,
    data: { isCmaReportModalOpen, cmaReports },
  } = useCmaReports({ person });

  const mostRecentReport = useMemo(() => {
    const viewedReports = cmaReports.filter((report) => report.last_viewed !== null);
    if (viewedReports.length > 1) {
      return viewedReports.sort((a, b) => new Date(b.last_viewed) - new Date(a.last_viewed))[0];
    }
    return cmaReports[0];
  }, [cmaReports]);

  const openLink = () => {
    window.open(
      "https://www.brivityknowledge.com/v1/docs/en/set-upedit-your-cma-profile?highlight=cma%20profile",
      "_blank",
    );
  };

  return (
    <CollapsibleElement
      id="pdp-cma-reports"
      title="CMA Reports"
      buttonDisabled={!hasEmail}
      buttonDisabledTooltip={personLoaded ? "Contact does not have an email address" : null}
      customButton={
        // A disabled Button can't be a PopoverTrigger child -- PopoverTrigger clones it as its own ref.
        hasEmail ? (
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <Button disabled={!hasApprovedMls}>+ Add</Button>
            </PopoverTrigger>
            <PopoverContent>
              <PopoverItem icon={<PlusSolidV6 />} onClick={() => window.open(person.cma_links[0], "_blank")}>
                New CMA Report
              </PopoverItem>
              <PopoverItem icon={<EyeSolidV6 />} onClick={() => window.open(person.cma_links[1], "_blank")}>
                View Report Status
              </PopoverItem>
            </PopoverContent>
          </Popover>
        ) : (
          <Button disabled>+ Add</Button>
        )
      }
    >
      {loading && <Loading />}
      <div id="person-market-reports-card">
        {!loading && (!hasApprovedMls || cmaReports.length === 0) ? (
          <div>
            {!loading && !hasApprovedMls && (
              <div className="tw-py-[12px] tw-px-[16px] tw-border-solid tw-border-[1px] tw-text-[#E5941B] tw-border-semantic-yellow-10 tw-bg-semantic-yellow-5 tw-rounded-sm tw-flex tw-items-center">
                <TriangleExclamationSolidV6 size="l" />
                <div>
                  <div className="tw-pl-[16px] tw-mb-[8px] tw-text-[12px] tw-leading-[16px] tw-text-semantic-yellow-120">
                    To start using CMA Reports, your CMA profile must be set up.
                  </div>
                  <Button schema="semantic-yellow" onClick={openLink} className="tw-ml-[16px]">
                    <span>SET UP MY PROFILE</span>
                  </Button>
                </div>
              </div>
            )}
            <div className={`tw-pt-[8px] tw-pb-[4px] ${css.personDetailEmptyStateText}`}>
              Create a new Comparative Market Analysis to create a detailed report of the property.
            </div>
          </div>
        ) : (
          <CmaReport
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...mostRecentReport}
            deleteReportFn={(id) => actions.deleteCmaReport({ id })}
            cmaUrl={cmaUrl}
          />
        )}

        {hasApprovedMls && cmaReports.length > 1 && (
          <TextButton onClick={() => actions.toggleModal(true)}>VIEW ALL</TextButton>
        )}

        <CmaReportPreview
          modalIsOpen={isCmaReportModalOpen}
          onClose={() => actions.toggleModal(false)}
          person={reportPreviewPerson}
          person_status={person.data?.attributes.status}
          person_address={
            person.included
              ?.filter((i) => i.type === "address" && i.id !== null)
              .map((i) => `${i.attributes?.street_address}`)[0]
          }
          person_lead_type={person.data?.attributes.lead_type?.charAt(0)}
          person_avatar={person.data?.attributes.avatar_url}
          cmaUrl={cmaUrl}
          deleteReportFn={(id) => actions.deleteCmaReport({ id })}
        />
      </div>
    </CollapsibleElement>
  );
};

CmaReports.propTypes = {
  hasApprovedMls: PropTypes.bool,
  loading: PropTypes.bool,
  person: PropTypes.shape({
    status: PropTypes.string,
    display_a_street_address: PropTypes.string,
    person_lead_type: PropTypes.string,
    person_avatar: PropTypes.string,
  }),
};
CmaReports.defaultProps = {
  hasApprovedMls: false,
  loading: false,
  person: {},
};

export default CmaReports;
